import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, Heart, ArrowRight, Home, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from '../utils/confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DonationSuccess = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/stripe/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
        confetti();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Ho fatto una donazione su Moneyfor!',
        text: 'Ho appena aiutato qualcuno in difficoltà su Moneyfor. Unisciti anche tu!',
        url: window.location.origin
      });
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-lg border border-stone-200 p-8 max-w-md w-full text-center"
      >
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-800 mx-auto mb-6"></div>
            <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">
              Verifica pagamento...
            </h1>
            <p className="text-stone-600">Stiamo verificando la tua donazione</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', bounce: 0.5 }}
              className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </motion.div>
            
            <h1 className="font-heading text-3xl font-bold text-stone-900 mb-2">
              {t('thankYou')}
            </h1>
            <p className="text-stone-600 mb-6">
              {t('donationSuccess')}
            </p>

            {paymentData && (
              <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-emerald-700 mb-1">Importo donato</p>
                <p className="font-heading text-3xl font-bold text-emerald-800">
                  {(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency.toUpperCase()}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Link to="/feed" className="w-full">
                <Button className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold">
                  <Home className="h-4 w-4 mr-2" />
                  Torna al feed
                </Button>
              </Link>
              
              <Button
                variant="outline"
                className="w-full h-12 rounded-xl"
                onClick={handleShare}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Condividi
              </Button>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Heart className="h-10 w-10 text-amber-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">
              Sessione scaduta
            </h1>
            <p className="text-stone-600 mb-6">
              La sessione di pagamento è scaduta. Riprova.
            </p>
            <Link to="/feed">
              <Button className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold">
                Torna al feed
              </Button>
            </Link>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Heart className="h-10 w-10 text-amber-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">
              Verifica in sospeso
            </h1>
            <p className="text-stone-600 mb-6">
              Non siamo riusciti a verificare il pagamento. Controlla la tua email per conferma.
            </p>
            <Link to="/feed">
              <Button className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold">
                Torna al feed
              </Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default DonationSuccess;
